import { useEffect, useState } from 'react';
import { audio } from '../engine/audio/audio';
import { useApp } from './store';
import { MainMenu } from './menus/MainMenu';
import { SkirmishSetup } from './menus/SkirmishSetup';
import { SettingsScreen } from './menus/SettingsScreen';
import { Credits } from './menus/Credits';
import { LoadScreen } from './menus/LoadScreen';
import { MultiplayerLobby } from './menus/MultiplayerLobby';
import { GameScreen } from './hud/GameScreen';
import { MenuScene } from './menus/MenuScene';
import { checkLiveBuild, liveBuild, openGate } from '../networking/gate';
import { trackOpen } from '../app/stats';

/**
 * Nothing runs until the server has let this copy of the game in (see networking/gate.ts). Local
 * development builds skip the check so the dev server and tests work without the Worker.
 */
export function App() {
  const [gate, setGate] = useState<'checking' | 'open' | 'closed'>(import.meta.env.DEV ? 'open' : 'checking');
  const check = () => {
    setGate('checking');
    void openGate(__APP_BUILD__).then((ok) => {
      useApp.setState({ liveBuild: liveBuild() });
      setGate(ok ? 'open' : 'closed');
    });
  };
  // Ask again every 10 minutes whether a newer build has gone live.
  useEffect(() => {
    if (import.meta.env.DEV) return;
    const t = setInterval(() => void checkLiveBuild(__APP_BUILD__).then((b) => useApp.setState({ liveBuild: b })), 10 * 60_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (gate === 'checking') check();
  }, []);
  useEffect(() => {
    if (gate === 'open') trackOpen();
  }, [gate]);
  if (gate !== 'open') {
    return (
      <>
        <div className="menu-backdrop" />
        <div className="menu-bg">
          <div className="gate-card">
            <h1 className="game-title">Four Crowns</h1>
            {gate === 'checking' ? (
              <p className="muted">Connecting…</p>
            ) : (
              <>
                <p>Four Crowns could not reach its server.</p>
                <p className="muted small">Check your internet connection, then try again.</p>
                <button className="btn primary" onClick={check}>Try again</button>
              </>
            )}
          </div>
        </div>
      </>
    );
  }
  return <Game />;
}

function Game() {
  const screen = useApp((s) => (/[?&](bench|showcase)=/.test(location.search) ? 'game' : s.screen));
  if (screen === 'game') return <Screen />;
  // One diorama behind all menus, so it keeps turning when moving between them.
  return (
    <>
      <div className="menu-backdrop" />
      <MenuScene />
      <Screen />
    </>
  );
}

function Screen() {
  const screen = useApp((s) => (/[?&](bench|showcase)=/.test(location.search) ? 'game' : s.screen));
  const prefs = useApp((s) => s.prefs);
  useEffect(() => {
    // Audio may only start after a user gesture (browser autoplay policy).
    // Different browsers count different events as permission to start sound, so listen to all of them
    // (capture phase, so nothing in the page can stop them first).
    const unlock = () => audio.unlock();
    const events = ['pointerdown', 'pointerup', 'mousedown', 'click', 'touchend', 'keydown'] as const;
    for (const e of events) window.addEventListener(e, unlock, true);
    return () => {
      for (const e of events) window.removeEventListener(e, unlock, true);
    };
  }, []);
  useEffect(() => audio.setVolumes(prefs.volume), [prefs.volume]);
  switch (screen) {
    case 'skirmish':
      return <SkirmishSetup />;
    case 'multiplayer':
      return <MultiplayerLobby />;
    case 'settings':
      return <SettingsScreen />;
    case 'credits':
      return <Credits />;
    case 'load':
    case 'replays':
      return <LoadScreen />;
    case 'game':
      return <GameScreen />;
    default:
      return <MainMenu />;
  }
}
